import { Collapse, Grid } from '@mui/material';
import { useFormState } from 'react-final-form';

import {
  Paper,
  Typography,
  CardContent,
  CardHeader,
  CalendarMonthOutlinedIcon,
  IconAvatar,
} from '@occc/ui-design-system';
import { Switches, KeyboardTimePicker, Select, ChipGroupField } from '@occc-app/design-system';
import { DateTimeFormatTZ, formatDateTimeTZ } from '@occc-app/lib/date';

import { FulfillmentWindowFormValues, OrderedWeekday, validTimezones } from './types';
import { DEFAULT_TIME_STEP_MINUTES } from './constants';

const options = Object.keys(OrderedWeekday).map((key) => ({
  label: key,
  value: (OrderedWeekday as any)[key],
}));

const timezoneOptions = validTimezones.map((timezone) => {
  const offset = formatDateTimeTZ(new Date(), timezone, DateTimeFormatTZ.TimeZoneOffset);
  const abbreviation = formatDateTimeTZ(new Date(), timezone, DateTimeFormatTZ.ShortTimeZone);
  const name = timezone.split('/')[1].replace('_', ' ');
  return {
    label: `${abbreviation} (GMT ${offset}) ${name}`,
    value: timezone,
  };
});

type FulfillmentWindowProps = {
  values?: FulfillmentWindowFormValues;
};

export const FulfillmentWindow = ({ values }: FulfillmentWindowProps) => {
  const formState = useFormState();

  return (
    <Paper>
      <CardHeader
        title={<Typography variant='subtitle1'>Set Fulfillment Window</Typography>}
        subheader={
          <Typography variant='body2' color='textSecondary'>
            Choose on what days of the week and what time of day this offer will be available
          </Typography>
        }
        action={<Switches name='fulfillmentWindow.isEnabled' data={{}} />}
        avatar={<IconAvatar Icon={CalendarMonthOutlinedIcon} />}
        sx={{ py: 2, px: 3 }}
      />
      <Collapse in={values?.fulfillmentWindow?.isEnabled} timeout='auto' unmountOnExit>
        <CardContent sx={{ pt: 0 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <KeyboardTimePicker
                label='Start Time'
                name='fulfillmentWindow.timeOfDay.availableDailyStartingAtTime'
                maxTime={formState.values?.fulfillmentWindow?.timeOfDay?.availableDailyEndingAtTime}
                timeSteps={{ minutes: DEFAULT_TIME_STEP_MINUTES }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KeyboardTimePicker
                label='End Time'
                name='fulfillmentWindow.timeOfDay.availableDailyEndingAtTime'
                minTime={
                  formState.values?.fulfillmentWindow?.timeOfDay?.availableDailyStartingAtTime
                }
                timeSteps={{ minutes: DEFAULT_TIME_STEP_MINUTES }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Select
                name='fulfillmentWindow.timezone'
                data={timezoneOptions}
                formControlProps={{ margin: 'none' }}
              />
            </Grid>
            <Grid item xs={12}>
              <ChipGroupField
                name='fulfillmentWindow.daysOfWeek.availableWeeklyOnDays'
                options={options}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Collapse>
    </Paper>
  );
};
